import { useContext, useEffect, useState, type ChangeEvent, type MouseEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SmartContext } from '../../../library/Core/SmartContext';
import { getDomainValueForCode, getFlexColDomainValueForCode, isEmpty, isSchoolAdmin, getBooleanFilterDefaults } from '../../../library/Core/SmartFunctions';
import AlumniDirectoryService from './AlumniDirectoryService';
import SessionContext from '../../../library/Core/SessionContext';
import { Tooltip } from 'bootstrap';
import FactLoader from '../../../library/SimpleControls/FactLoader';
import DirectoryFilterDomainCheckboxes, { DirectoryShowProfileSection } from '../shared/DirectoryFilterDomainCheckboxes';
import { getActiveShowProfileFiltersFromConfig } from '../shared/directoryAppliedFilters';
import { isProfessionalDetailsQuery } from '../../admin/profile-analytics-dashboard/directoryLinks';

export const handleClearAllFilterClick = (
    event: MouseEvent<HTMLElement>, // more generic
    state: any,
    dispatch: any
  ) => {
    event.preventDefault();
  
    if (state.data.searchBy.length > 0) {
      dispatch({
        type: 'CONTROL_VALUE_CHANGE',
        payload: { dataKey: 'filters.context.searchBy', value: '' },
      });
    }
  
    const defaultSearchCriteria = AlumniDirectoryService.getDefaultSearchCriteria('');
    const criteriaForSelectedCard = AlumniDirectoryService.getCriteriaForSelectedCard('totalJoinedAlumni', state);
    let criteria = { ...defaultSearchCriteria, ...criteriaForSelectedCard };
    criteria = { ...criteria,'context': { ...criteria.context, selectedCard : 'totalJoinedAlumni' } };
  
    dispatch({
      type: 'CONTROL_VALUE_CHANGE',
      payload: { dataKey: 'filters', value: criteria },
    });

    // Signal DirectorySearchControl to clear per-column filter search text (e.g. Batch "2001")
    dispatch({
      type: 'CONTROL_VALUE_CHANGE',
      payload: { dataKey: 'filterSearchTermsResetToken', value: Date.now() },
    });

    AlumniDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch);

    // Note: After the initial load, Juniors, Seniors, Batch mates run based on the selected criteria. So removing the searchBy.
    if (state.data.searchBy.length > 0) {
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'filters.context.searchBy', value: '' } });
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'searchBy', value: '' } });
    }
};  

interface ExpandedControls {
    [key: string]: boolean;
}

type YearDomain = { code: string | number; value: string };

// Decade-bucket helper (same logic as DomainExpertSearchControl / RecruiterSearchControl)
const makeDecadeBuckets = (domains: YearDomain[], foundingYear: number = 1950) => {
    if (!domains?.length) return { beforeLabel: '', beforeYears: [] as YearDomain[], groups: [] as any[] };

    const currentYear = new Date().getFullYear();
    const nums = domains.map((d) => parseInt(String(d.code), 10)).filter(Number.isFinite).sort((a, b) => a - b);
    if (!nums.length) return { beforeLabel: '', beforeYears: [], groups: [] };

    const minYear = nums[0];
    const maxYear = nums[nums.length - 1];
    const startDecade = Math.floor(Math.max(foundingYear, minYear) / 10) * 10;
    const endDecade = Math.floor(maxYear / 10) * 10;

    const beforeYears = domains
        .filter((d) => parseInt(String(d.code), 10) < startDecade)
        .sort((a, b) => parseInt(String(a.code), 10) - parseInt(String(b.code), 10));

    const groups: any[] = [];
    for (let d = startDecade; d <= endDecade; d += 10) {
        const to = d === endDecade ? currentYear : d + 9;
        const label = d === endDecade ? `${d}-${currentYear}` : `${d}-${d + 9}`;
        const years = domains
            .filter((x) => {
                const y = parseInt(String(x.code), 10);
                return y >= d && y <= (d === endDecade ? currentYear : d + 9);
            })
            .sort((a, b) => parseInt(String(a.code), 10) - parseInt(String(b.code), 10));
        if (years.length) groups.push({ from: d, to, label, years });
    }

    return {
        beforeLabel: beforeYears.length ? `Before ${startDecade}` : '',
        beforeYears,
        groups,
    };
};

const DirectorySearchControl = () => {
    const uniqueCodes = new Set<string>();
    const { state, dispatch } = useContext(SmartContext);
    const [searchParams] = useSearchParams();
    //const userTypeCode = sessionStorage.getItem('user-type-code');
    const { sessionState, sessionDispatch } = useContext(SessionContext);
    const userTypeCode = sessionState?.userTypeCode;
    const [performSearch, setPerformSearch] = useState(0);
    const [loading, setLoading] = useState(false);
    const [displayMode, setDisplayMode] = useState('CURRENT');
    const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
    const [searchTerms, setSearchTerms] = useState({} as any);
    const filterSearchTermsResetToken = state?.data?.filterSearchTermsResetToken;
    useEffect(() => {
        if (filterSearchTermsResetToken) {
            setSearchTerms({});
        }
    }, [filterSearchTermsResetToken]);
    const [showFilters, setShowFilters] = useState(true);
    const schoolTypes = ['SCHOOL', 'PRE_SCHOOL', 'K12_SCHOOL'];
    // const [isUGSelected, setIsUGSelected] = useState(sessionState?.instituteTypeCode === 'K12_SCHOOL' ? true : false); // if institute type will be K12 then by default UG will be selected else PG
    // const [isUGSelected, setIsUGSelected] = useState(schoolTypes.includes(sessionState?.instituteTypeCode ?? '') ? true : false);
    // ?pgUniversityId=<id> on the route pre-selects PG-side university filters (see
    // AlumniDirectory.tsx) — flip the toggle to PG on load so it reflects that filter
    // instead of silently showing UG with the switch still pointed at UG.
    const [isUGSelected, setIsUGSelected] = useState(!(state?.data?.filters?.pgUniversityId?.length > 0)); //default UG selected for all, unless a pgUniversityId filter is preloaded

    //const searchFilterDomains = state?.data?.searchDomain;
    const [searchFilterDomains, setSearchFilterDomains] = useState(state?.data?.searchDomain);
    const ugUniversityId = state?.data?.searchDomain?.universityId;
    const pgUniversityId = state?.data?.searchDomain?.pgUniversityId;

    const collegeCountryCode = state?.data?.searchDomain?.collegeCountryCode;
    const pgCollegeCountryCode = state?.data?.searchDomain?.pgCollegeCountryCode;

    let displayClearAll = false;

    // Clone the filterConfig to modify it
    const modifiedFilterConfig = { ...AlumniDirectoryService.filterConfig };
    let instituteTypeCode;
    console.log(sessionState);
    if(isSchoolAdmin()){
        const flexCol = getFlexColDomainValueForCode(sessionState?.primaryInstitute, 'INSTITUTE_DOMAIN', state);
        instituteTypeCode = isEmpty(flexCol) ? '' : JSON.parse(flexCol).institute_type;
    }else{
        instituteTypeCode=sessionState?.instituteTypeCode
    }

    console.log("instituteTypeCode",instituteTypeCode)
    
    // Update isAdditional property for entranceTestCode
    if (schoolTypes.includes(instituteTypeCode)) {
        if (modifiedFilterConfig['entranceTestCode']) {
            modifiedFilterConfig['entranceTestCode'].isAdditional = true;
        }
        if (modifiedFilterConfig['fieldOfStudyCode']) {
            modifiedFilterConfig['fieldOfStudyCode'].isVisible = true;
        }
        if (modifiedFilterConfig['collegeCountryCode']) {
            modifiedFilterConfig['collegeCountryCode'].isVisible = true;
        }
        // For Designation level WIP -> K-12 Alumni
        if (modifiedFilterConfig['seniorityLevelCode'] && sessionState?.userTypeCode==='ALUMNI') {
            modifiedFilterConfig['seniorityLevelCode'].isVisible = true;
        }
    }else{  //College Admin, College Student and College Alumni
        if (modifiedFilterConfig['seniorityLevelCode']) {
            modifiedFilterConfig['seniorityLevelCode'].isVisible = true;
        }
    }

    // Deep-links from Profile Analytics (industry / functional area / seniority) live under
    // additional filters — surface them in the main row so the selected values are visible.
    if (searchParams.get('industryCode') && modifiedFilterConfig['industryCode']) {
        modifiedFilterConfig['industryCode'].isAdditional = false;
    }
    if (searchParams.get('functionalAreaCode') && modifiedFilterConfig['functionalAreaCode']) {
        modifiedFilterConfig['functionalAreaCode'].isAdditional = false;
    }
    if ((searchParams.get('seniorityLevelCode') || isProfessionalDetailsQuery(searchParams)) && modifiedFilterConfig['seniorityLevelCode']) {
        modifiedFilterConfig['seniorityLevelCode'].isAdditional = false;
        modifiedFilterConfig['seniorityLevelCode'].isVisible = true;
    }

    const filters: any = Object.values(modifiedFilterConfig).filter(
        (filter: any) => filter.applicableFor === userTypeCode || filter.applicableFor === 'BOTH'
    );

    // const filters: any = Object.values(AlumniDirectoryService.filterConfig).filter(
    //     (filter: any) => filter.applicableFor === userTypeCode || filter.applicableFor === 'BOTH'
    // );

     useEffect(() => {
        var tooltipTriggerList = [].slice.call(document.querySelectorAll('.defaultTooltip'));
        var tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl);
        });

        // Set the default UG/PG flag directly without bumping `performSearch`.
        // The initial search effect ([performSearch] === 0) already fires once on
        // mount; using updateFilters here would bump performSearch and trigger a
        // redundant second /search call on load. higherStudyAtUG only affects the
        // query when a fieldOfStudyCode filter is selected (none on load), so the
        // single initial search is unchanged.
        dispatch({
            type: 'CONTROL_VALUE_CHANGE',
            payload: { dataKey: `filters.higherStudyAtUG`, value: isUGSelected, errorMessages: [] },
        });

        if (!isUGSelected) {
            // Set search domains to PG university options
            setSearchFilterDomains((prevDomains: any) => ({
                ...prevDomains,
                universityId: pgUniversityId, // Load PG university options
                collegeCountryCode : pgCollegeCountryCode
            }));
        }
    }, []);

    // Additional filters render lazily behind a toggle, so any tooltip-enabled
    // items they contain don't exist yet when the mount-time init above runs.
    // Re-init tooltips for that subtree once it's shown.
    useEffect(() => {
        if (!showAdditionalFilters) return;
        var tooltipTriggerList = [].slice.call(document.querySelectorAll('#additionalfilter .defaultTooltip'));
        tooltipTriggerList.forEach((tooltipTriggerEl: any) => {
            if (!Tooltip.getInstance(tooltipTriggerEl)) {
                new Tooltip(tooltipTriggerEl);
            }
        });
    }, [showAdditionalFilters]);

    const handleToggleChange = (event: any) => {
        const isChecked = event.target.checked;
        console.log("isChecked",isChecked)
        setIsUGSelected(isChecked); // Update the state based on toggle selection
        updateFilters('CONTROL_VALUE_CHANGE', `filters.higherStudyAtUG`, isChecked);

        if (isChecked === false) {
            // Assuming false means PG is selected
            dispatch({
                type: 'CHECKBOX_DELETE_ALL_RECORD_IN_ARRAY',
                payload: {
                    dataKey: 'filters.universityId',
                    value: null, // Not needed for clearing
                },
            });

            dispatch({
                type: 'CHECKBOX_DELETE_ALL_RECORD_IN_ARRAY',
                payload: {
                    dataKey: 'filters.collegeCountryCode',
                    value: null, // Not needed for clearing
                },
            });
            // Optionally reset UG selections or update state as needed

            setSearchFilterDomains({
                ...searchFilterDomains,
                universityId: pgUniversityId,
                collegeCountryCode: pgCollegeCountryCode,
            });
        } else {
            dispatch({
                type: 'CHECKBOX_DELETE_ALL_RECORD_IN_ARRAY',
                payload: {
                    dataKey: 'filters.pgUniversityId',
                    value: null, // Not needed for clearing
                },
            });
            dispatch({
                type: 'CHECKBOX_DELETE_ALL_RECORD_IN_ARRAY',
                payload: {
                    dataKey: 'filters.pgCollegeCountryCode',
                    value: null, // Not needed for clearing
                },
            });
            setSearchFilterDomains({
                ...searchFilterDomains,
                universityId: ugUniversityId,
                collegeCountryCode: collegeCountryCode,
            });
        }

        // Trigger search with updated filters
        setPerformSearch((prev) => prev + 1);
    };

    useEffect(() => {
        let isActive = true;

        // pageLoader already fired the default search in parallel with other page-config
        // calls and stored results in state.data.students — skip the duplicate /search
        // call so the user sees results immediately without a second round-trip.
        if (performSearch === 0 && state.data.preloadedSearch) {
            setLoading(false);
            return () => { isActive = false; };
        }

        setLoading(true);

        const criteria =
            performSearch === 0
                ? { ...AlumniDirectoryService.getCriteriaForSelectedCard('totalJoinedAlumni', state), ...state.data.filters }
                : state.data.filters;

        AlumniDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch)
            .finally(() => { if (isActive) setLoading(false); });

        // Note: After the initial load, Juniors, Seniors, Batch mates run based on the selected criteria. So removing the searchBy.
        if (state.data.searchBy.length > 0) {
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'filters.context.searchBy', value: '' } });
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'searchBy', value: '' } });
        }

        return () => { isActive = false; };
    }, [performSearch]);

    const updateFilters = (type: string, dataKey: string, value: any) => {
        dispatch({ type, payload: { dataKey, value, errorMessages: [] } });
        setPerformSearch((prev) => prev + 1);
    };

    const handleSearchListItemSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const { id, value, checked } = event.target;
        console.log("id-----",id)
        const type = checked ? 'CHECKBOX_ADD_NEW_RECORD_IN_ARRAY' : 'DELETE_RECORD_IN_SIMPLE_ARRAY';

        let updatedId = id; // Declare a new variable to hold the updated id for UG/PG heigher study paramater
        if (id === 'pgUniversityId' || id === 'universityId') {
            if (isUGSelected === false) {
                // updateFilters('CHECKBOX_ADD_NEW_RECORD_IN_ARRAY', `filters.pgUniversityId`, value);
                updateFilters('CHECKBOX_DELETE_ALL_RECORD_IN_ARRAY', `filters.universityId`, null);
                updateFilters(type, `filters.pgUniversityId`, value); // Use updatedId in the function call
                return;
                //dispatch({ type: 'DELETE_RECORD_IN_SIMPLE_ARRAY', payload: { dataKey: 'filters.universityId', value: [] } });

                // updateFilters(`CHECKBOX_ADD_NEW_RECORD_IN_ARRAY`, `filters.universityId`, []); // Use updatedId in the function call
                //updateFilters('CONTROL_VALUE_CHANGE', `filters.universityId`, event.target.checked ? value : null);
                //updatedId = 'pgUniversityId'; // Update the id if isUGSelected is false, if value is false then in param we will send pgUniversityId filter
            } else if (isUGSelected === true) {
                updateFilters('CHECKBOX_DELETE_ALL_RECORD_IN_ARRAY', `filters.pgUniversityId`, null);
                // updateFilters('DELETE_RECORD_IN_SIMPLE_ARRAY', `filters.universityId`, value);
                // updateFilters('CHECKBOX_ADD_NEW_RECORD_IN_ARRAY', `filters.universityId`, value);

                updateFilters(type, `filters.universityId`, value); // Use updatedId in the function call
                return;
            }
        } if (id==='collegeCountryCode' || id==='pgCollegeCountryCode') {
            if (isUGSelected === false) {
                updateFilters('CHECKBOX_DELETE_ALL_RECORD_IN_ARRAY', `filters.collegeCountryCode`, null);
                updateFilters(type, `filters.pgCollegeCountryCode`, value); // Use updatedId in the function call

                return;
               
            } else if (isUGSelected === true) {
                // FOr country
                updateFilters('CHECKBOX_DELETE_ALL_RECORD_IN_ARRAY', `filters.pgCollegeCountryCode`, null);
                updateFilters(type, `filters.collegeCountryCode`, value); // Use updatedId in the function call
                return;
            }
        }else {
            updateFilters(type, `filters.${updatedId}`, value); // Use updatedId in the function call
        }
    };

    const handleShowProfileFilterChange = (filterKey: string) => (event: ChangeEvent<HTMLInputElement>) => {
        const checkedValue = modifiedFilterConfig[filterKey]?.booleanCheckedValue ?? 1;
        updateFilters('CONTROL_VALUE_CHANGE', `filters.${filterKey}`, event.target.checked ? checkedValue : null);
    };

    const handleSimilarSkillsSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const skills = event.target.checked ? AlumniDirectoryService.getSkills() : [];
        updateFilters('CONTROL_VALUE_CHANGE', `filters.skills`, skills);
    };

    const handleSameNextLevelInterestsSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const skills = event.target.checked ? AlumniDirectoryService.getSkillToTarget() : [];
        updateFilters('CONTROL_VALUE_CHANGE', `filters.skillToTarget`, skills);
    };

    const handleShowAdditionalFilter = (event: any) => {
        event.preventDefault();
        setShowAdditionalFilters(!showAdditionalFilters);
    };

    const handleSearchChange = (controlId: string) => (event: ChangeEvent<HTMLInputElement>) => {
        setSearchTerms((prevSearchTerms: any) => ({ ...prevSearchTerms, [controlId]: event.target.value }));
    };

    const getDomainCategoryCodeForKey = (key: string) => {
        // Fall back to the static filterConfig (same source the checkbox list itself is
        // rendered from) when the page-config lookup below doesn't have an entry yet —
        // e.g. a filter pre-selected from a URL query param on the very first render,
        // before any search-triggered re-render has happened.
        const fromFormConfig = state.formConfig?.sectionRepository
            .find((section) => section.id === 'filters')
            ?.controlGroup?.find((control) => control.id === key)?.props.domainCategoryCode;
        return fromFormConfig ?? AlumniDirectoryService.filterConfig[key]?.domain ?? '';
    };

    const handleAppliedFilterClick = (event: any, key: string, value: string) => {
        event.preventDefault();

        // Remove the searchBy after the initial load. User can remove the years.
        if (state.data.searchBy.length > 0) updateFilters('CONTROL_VALUE_CHANGE', `filters.context.searchBy`, '');

        updateFilters('DELETE_RECORD_IN_SIMPLE_ARRAY', `filters.${key}`, value);
    };

    const handleBooleanFilterClear = (event: MouseEvent, key: string) => {
        event.preventDefault();
        if (state.data.searchBy.length > 0) updateFilters('CONTROL_VALUE_CHANGE', `filters.context.searchBy`, '');
        updateFilters('CONTROL_VALUE_CHANGE', `filters.${key}`, null);
    };

    const handleCardViewModeChange = () => {
        const cardViewMode = state.data?.cardViewMode === 'CURRENT' ? 'OUTLOOK' : 'CURRENT';
        setDisplayMode(cardViewMode);
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'cardViewMode', value: cardViewMode } });
    };

    const [isOpen, setOpen] = useState(false);

    const handleFilterToggle = () => {
        setOpen(!isOpen);
        setShowAdditionalFilters(!showAdditionalFilters);
    };

    const [expandedControls, setExpandedControls] = useState<ExpandedControls>({});
    const [expandedDecades, setExpandedDecades] = useState<Record<string, boolean>>({});

    // Function to toggle the expanded state of a control
    const toggleControl = (controlId: string) => {
        console.log('controlId', controlId);
        setExpandedControls((prevExpandedControls) => ({
            [controlId]: !prevExpandedControls[controlId],
        }));
    };

    const toggleDecade = (label: string) => {
        setExpandedDecades((prev) => ({ ...prev, [label]: !prev[label] }));
    };

    const getSelectedPassOutYears = () => (state?.data?.filters?.passOutYear || []).map(String);

    const isYearGroupFullySelected = (years: YearDomain[]) => {
        const selected = getSelectedPassOutYears();
        return years.length > 0 && years.every((y) => selected.includes(String(y.code)));
    };

    const isYearGroupPartiallySelected = (years: YearDomain[]) => {
        const selected = getSelectedPassOutYears();
        const selectedCount = years.filter((y) => selected.includes(String(y.code))).length;
        return selectedCount > 0 && selectedCount < years.length;
    };

    const handleYearGroupSelectAll = (label: string, years: YearDomain[]) => (event: ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        const yearCodes = years.map((y) => String(y.code));
        const selected = getSelectedPassOutYears();
        const allSelected = yearCodes.every((c) => selected.includes(c));
        const next = allSelected
            ? selected.filter((c: string) => !yearCodes.includes(c))
            : [...new Set([...selected, ...yearCodes])];
        setExpandedDecades((prev) => ({ ...prev, [label]: !allSelected }));
        updateFilters('CONTROL_VALUE_CHANGE', 'filters.passOutYear', next);
    };

    const yearCheckbox = (yd: YearDomain) => {
        const codeStr = String(yd.code);
        const checked = getSelectedPassOutYears().includes(codeStr);
        return (
            <div key={codeStr} className="form-check d-flex flex-wrap ms-1">
                <input
                    id="passOutYear"
                    className="form-check-input me-2"
                    type="checkbox"
                    value={yd.code}
                    name="passOutYear_Name"
                    checked={checked}
                    onChange={handleSearchListItemSelect}
                    onClick={(e) => e.stopPropagation()}
                />
                <label className="form-check-label font-16 font-400 flex-1 pt-1" onClick={(e) => e.stopPropagation()}>
                    {yd.value}
                </label>
            </div>
        );
    };

    const renderYearGroupRow = (label: string, years: YearDomain[]) => {
        const open = !!expandedDecades[label];
        const allSelected = isYearGroupFullySelected(years);
        const partiallySelected = isYearGroupPartiallySelected(years);
        return (
            <div key={label} className="mb-2">
                <div
                    className={`form-check d-flex flex-wrap align-items-center parent ${open ? '' : 'collapsed'}`}
                    role="button"
                    aria-expanded={open}
                    onClick={() => toggleDecade(label)}>
                    <input
                        className="form-check-input me-2"
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                            if (el) el.indeterminate = partiallySelected;
                        }}
                        onChange={handleYearGroupSelectAll(label, years)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select all ${label}`}
                    />
                    <label className="form-check-label font-16 font-400 flex-1 pt-1">{label}</label>
                </div>
                {open && <div className="child ps-4 show">{years.map(yearCheckbox)}</div>}
            </div>
        );
    };

    const renderPassOutYearGroups = () => {
        const src: YearDomain[] = searchFilterDomains?.['passOutYear'] ?? [];
        const q = (searchTerms['passOutYear'] || '').toLowerCase();
        const filtered = !q
            ? src
            : src.filter(
                  (d) =>
                      String(d.value).toLowerCase().includes(q) ||
                      String(d.code).toLowerCase().includes(q)
              );
        const { beforeLabel, beforeYears, groups } = makeDecadeBuckets(filtered);
        return (
            <>
                {beforeLabel && beforeYears.length > 0 && renderYearGroupRow(beforeLabel, beforeYears)}
                {groups.map((g) => renderYearGroupRow(g.label, g.years))}
            </>
        );
    };

    console.log('expandedControls', expandedControls);

    return (
        <>
            <FactLoader show={loading} />
            <div>
            <section className="alumni_directory_filter container-fluid">
                <div className="max-1140 mx-auto">
                    <div className="JobInternshipListing">
                        <div className="ji_filtersec aos-init aos-animate">
                            <div className="mobileFilter d-flex justify-content-between align-items-center d-md-none mobileonly text-end">
                                <button
                                    className="btn-filter btn-unscroll font-18 font-500 w-100"
                                    type="button"
                                    onClick={handleFilterToggle}
                                    aria-expanded="true"
                                    aria-controls="filter_collapse">
                                    <i className="icon-filter"></i>
                                    <span>Filters</span>
                                    {/* <span> Selected Options (8)<i className="icon-right-arrow"></i></span> */}
                                </button>
                            </div>

                            <div className={`accordion collapse show directory_wrap ${isOpen ? 'open' : ''}`} id="filter_collapse">
                                <div className="filters-head">
                                    <div className="filters-head-left">
                                        <h3
                                            className="font-18 font-500 mb-0"
                                            role="button"
                                            onClick={() => setShowFilters(!showFilters)}
                                            data-bs-toggle="collapse"
                                            data-bs-target="#accordionFilters"
                                            aria-controls="accordionFilters"
                                            aria-expanded={showFilters ? 'true' : 'false'}>
                                            <span className="icon-filter"></span>
                                            <span>Filters</span>
                                        </h3>
                                        <div
                                            className={`badge rounded-pill text-bg-secondary py-2 px-3 bg-opacity-25 text-dark mt-3 mt-md-0 ${state?.flags?.isDataLoading ? ' is-loading' : ''}`}
                                            id="UsersCountsTop"
                                            aria-live="polite">
                                            <div className="directory-result-count__body">
                                                {state?.flags?.isDataLoading ? (
                                                    <span className="directory-result-count__label">Searching…</span>
                                                ) : (
                                                    <span className="directory-result-count__row">
                                                        <span className="directory-result-count__num font-14 font-500">
                                                            {(
                                                                (state?.data as any)?.pagination?.total != null
                                                                    ? (state.data as any).pagination.total
                                                                    : state?.data?.students?.length ?? 0
                                                            ).toLocaleString()}
                                                        </span>
                                                        <span className="directory-result-count__label font-14 font-400">
                                                            {' '}
                                                            alumni found
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <a
                                        className="btn-clear btn-tab-clear d-block d-md-none"
                                        role="button"
                                        onClick={(e) => handleClearAllFilterClick(e, state, dispatch)}>
                                        Clear
                                    </a>
                                    <div className="filters-head-right">
                                        <div className="filter-switch-wrap">
                                            <div className="form-check form-switch filter-switch d-flex align-items-center p-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="button"
                                                    onClick={handleCardViewModeChange}
                                                    id="flexSwitchView"
                                                    checked={state?.data?.cardViewMode === 'CURRENT' ? true : false}
                                                />
                                                <label className="CURRENTView" htmlFor="flexSwitchView">
                                                    Current View
                                                </label>
                                                <label className="outcomeView" htmlFor="flexSwitchView">
                                                    Outcome View
                                                </label>
                                            </div>
                                            <b
                                                className="icon-info ms-2 text-dark-blue defaultTooltip"
                                                data-bs-toggle="tooltip"
                                                title="The outcome view shows what path the alumni took up after the program completion, while the current view shows their latest status regarding education or work."></b>
                                        </div>
                                        <i
                                            className="btn-md-filter icon-right-arrow d-none d-md-block filters-head-toggle"
                                            role="button"
                                            onClick={() => setShowFilters(!showFilters)}
                                            data-bs-toggle="collapse"
                                            data-bs-target="#accordionFilters"
                                            aria-controls="accordionFilters"
                                            aria-expanded={showFilters ? 'true' : 'false'}></i>
                                    </div>
                                </div>

                                {showFilters && (
                                    <div
                                        className="accordion-collapse collapse show"
                                        id="accordionFilters"
                                        data-bs-parent="filter_collapse">
                                        <div className="filterGroup mt-3">
                                            {Object.values(filters)
                                                .filter((control: any) => !control.isAdditional && control.isVisible && !control.isShowProfileFilter)
                                                .map((control: any, index: number) => (
                                                    <div key={control.id} className={control.class}>
                                                        <div className="filterHeader">
                                                            <div className="d-flex justify-content-between align-items-center position-relative">
                                                                <button
                                                                    className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center"
                                                                    type="button"
                                                                    onClick={() => toggleControl(control.id)}
                                                                    data-bs-toggle="collapse"
                                                                    data-bs-target={`#collapse${control.id}`}
                                                                    aria-expanded={
                                                                        !isOpen
                                                                            ? 'true'
                                                                            : isOpen && (index === 0 || expandedControls[control.id])
                                                                            ? 'true'
                                                                            : 'false'
                                                                    }>
                                                                    <span className="font-18 font-600">{control.label}</span>
                                                                </button>
                                                                {control.id === 'universityId' ? (
                                                                    <div className="filter-switch-wrap d-none d-md-flex">
                                                                        <div className="form-check form-switch filter-switch d-flex align-items-center p-0">
                                                                            <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                checked={isUGSelected}
                                                                                onChange={handleToggleChange}
                                                                                role="switch"
                                                                                id="flexSwitchHigherStudies"
                                                                            />
                                                                            <label
                                                                                className="currentView"
                                                                                htmlFor="flexSwitchHigherStudies">
                                                                                UG
                                                                            </label>
                                                                            <label
                                                                                className="outcomeView"
                                                                                htmlFor="flexSwitchHigherStudies">
                                                                                PG
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    ''
                                                                )}
                                                            </div>
                                                        </div>
                                                        {/* <div className="collapse show" id={`collapse${control.id}`}> */}
                                                        <div
                                                            id={`collapse${control.id}`}
                                                            className={`accordion-collapse collapse ${
                                                                !isOpen
                                                                    ? 'show'
                                                                    : isOpen && (index === 0 || expandedControls[control.id])
                                                                    ? 'show'
                                                                    : ''
                                                            }`}
                                                            data-bs-parent="#accordionFilters">
                                                            {/* mobile view toogle UG/PG */}
                                                            {control.id === 'universityId' ? (
                                                                <div className="filter-switch-wrap d-flex justify-content-end d-md-none w-100 position-static mb-2">
                                                                    <div className="form-check form-switch filter-switch d-flex align-items-center p-0">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            checked={isUGSelected}
                                                                            onChange={handleToggleChange}
                                                                            role="switch"
                                                                            id="flexSwitchHigherStudies"
                                                                        />
                                                                        <label className="currentView" htmlFor="flexSwitchHigherStudies">
                                                                            UG
                                                                        </label>
                                                                        <label className="outcomeView" htmlFor="flexSwitchHigherStudies">
                                                                            PG
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                ''
                                                            )}

                                                            <div className="filterSearchblk">
                                                                <form className="d-flex align-items-center filterSearch position-relative">
                                                                    <input
                                                                        className="form-control flex-1 font-14 font-500 w-100"
                                                                        type="search"
                                                                        placeholder="Search"
                                                                        aria-label="Search"
                                                                        name={`${control.id}_Search`}
                                                                        id={`${control.id}_Search`}
                                                                        value={searchTerms[control.id] || ''}
                                                                        onChange={handleSearchChange(control.id)}
                                                                    />
                                                                    <button
                                                                        className="btn icon-search"
                                                                        type="button"
                                                                        id={`${control.id}_Btn`}></button>
                                                                </form>
                                                            </div>
                                                            <div className="card card-body">
                                                                <div className="overflow-hidden">
                                                                    <div className="scrollMe" id="PassoutYear_Searchdiv">
                                                                        {control.id === 'passOutYear' ? (
                                                                            renderPassOutYearGroups()
                                                                        ) : (
                                                                            <DirectoryFilterDomainCheckboxes
                                                                                controlId={control.id}
                                                                                domainCategory={control.domain}
                                                                                controlConfig={control}
                                                                                items={searchFilterDomains[control.id]}
                                                                                searchTerm={searchTerms[control.id] || ''}
                                                                                dedupeCodes={control.id === 'programId' ? uniqueCodes : undefined}
                                                                                isChecked={(code) =>
                                                                                    (control.id === 'universityId' || control.id === 'collegeCountryCode')
                                                                                        ? state?.data?.filters[
                                                                                              isUGSelected
                                                                                                  ? control.id
                                                                                                  : control.id === 'universityId'
                                                                                                  ? 'pgUniversityId'
                                                                                                  : 'pgCollegeCountryCode'
                                                                                          ]
                                                                                              ?.map(String)
                                                                                              .includes(String(code))
                                                                                        : state?.data?.filters[control.id]
                                                                                              ?.map(String)
                                                                                              .includes(String(code))
                                                                                }
                                                                                onChange={handleSearchListItemSelect}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                            <DirectoryShowProfileSection
                                                title="Show Profiles"
                                                filterConfig={modifiedFilterConfig}
                                                filterValues={state?.data?.filters}
                                                onBooleanChange={handleShowProfileFilterChange}
                                                part="desktop"
                                            />

                                            <div className="additionalfilter_wrap last mb-20">
                                                <a
                                                    href="javascript:;"
                                                    className="btn-additionalfilter my-3 collapsed"
                                                    onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
                                                    data-bs-toggle="collapse"
                                                    data-bs-target="#additionalfilter"
                                                    aria-controls="additionalfilter"
                                                    aria-expanded={showAdditionalFilters ? 'true' : 'false'}>
                                                    <span>ADDITIONAL FILTER</span>
                                                    <i className="btn-md-filter icon-right-arrow d-none d-md-block"></i>
                                                </a>
                                                {showAdditionalFilters && (
                                                    <div className="collapse show" id="additionalfilter">
                                                        <div className="filterGroup">
                                                            {Object.values(filters)
                                                                .filter((control: any) => control.isAdditional)
                                                                .map((control: any, index: number) => (
                                                                    <div key={control.id} className={control.class}>
                                                                        <div className="filterHeader">
                                                                            <div className="d-flex justify-content-between align-items-center position-relative">
                                                                                {/* <button
                                                                                    className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center"
                                                                                    type="button"
                                                                                    data-bs-toggle="collapse"
                                                                                    data-bs-target={`#collapse${control.id}`}
                                                                                    aria-expanded={!isOpen ? 'true' : 'false'}
                                                                                    aria-controls={`collapse${control.id}`}>
                                                                                    <span className="font-18 font-600">
                                                                                        {control.label}
                                                                                    </span>
                                                                                </button> */}
                                                                                <button
                                                                                    className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center"
                                                                                    type="button"
                                                                                    onClick={() => toggleControl(control.id)}
                                                                                    data-bs-toggle="collapse"
                                                                                    data-bs-target={`#collapse${control.id}`}
                                                                                    aria-expanded={
                                                                                        !isOpen
                                                                                            ? 'true'
                                                                                            : isOpen && expandedControls[control.id]
                                                                                            ? 'true'
                                                                                            : 'false'
                                                                                    }
                                                                                    aria-controls={`collapse${control.id}`}>
                                                                                    <span className="font-18 font-600">
                                                                                        {control.label}
                                                                                    </span>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        {/* <div className="collapse show" id={`collapse${control.id}`}> */}
                                                                        <div
                                                                            id={`collapse${control.id}`}
                                                                            className={`accordion-collapse collapse ${
                                                                                !isOpen
                                                                                    ? 'show'
                                                                                    : expandedControls[control.id] && isOpen
                                                                                    ? 'show'
                                                                                    : ''
                                                                            }`}
                                                                            data-bs-parent="#accordionFilters">
                                                                            <div className="filterSearchblk">
                                                                                <form className="d-flex align-items-center filterSearch position-relative">
                                                                                    <input
                                                                                        className="form-control flex-1 font-14 font-500 w-100"
                                                                                        type="search"
                                                                                        placeholder="Search"
                                                                                        aria-label="Search"
                                                                                        name={`${control.id}_Search`}
                                                                                        id={`${control.id}_Search`}
                                                                                        value={searchTerms[control.id] || ''}
                                                                                        onChange={handleSearchChange(control.id)}
                                                                                    />
                                                                                    <button
                                                                                        className="btn icon-search"
                                                                                        type="button"
                                                                                        id={`${control.id}_Btn`}></button>
                                                                                </form>
                                                                            </div>
                                                                            <div className="card card-body">
                                                                                <div className="overflow-hidden">
                                                                                    <div className="scrollMe" id="PassoutYear_Searchdiv">
                                                                                        <DirectoryFilterDomainCheckboxes
                                                                                            controlId={control.id}
                                                                                            domainCategory={control.domain}
                                                                                            controlConfig={control}
                                                                                            items={searchFilterDomains[control.id]}
                                                                                            searchTerm={searchTerms[control.id] || ''}
                                                                                            isChecked={(code) =>
                                                                                                state?.data?.filters[control.id]
                                                                                                    ?.map(String)
                                                                                                    .includes(String(code))
                                                                                            }
                                                                                            onChange={handleSearchListItemSelect}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <DirectoryShowProfileSection
                                                title="Show Profiles"
                                                filterConfig={modifiedFilterConfig}
                                                filterValues={state?.data?.filters}
                                                onBooleanChange={handleShowProfileFilterChange}
                                                part="mobile"
                                            />

                                        </div>
                                    </div>
                                )}
                                <div className="d-flex d-md-none filter_close_apply">
                                    <a
                                        className="btn-filter-close collapsed"
                                        onClick={handleFilterToggle}
                                        href="javascript:;"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#filter_collapse"
                                        aria-expanded="false"
                                        aria-controls="filter_collapse">
                                        Close
                                    </a>
                                    {/* <a className="btn-filter-apply" href="javascript:;">Apply</a> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section>
                <div className="max-1140 mx-auto mb-3">
                    <div className="row">
                        <div className="filterresult border-0 mb-0">
                            <ul>
                                {Object.keys(state?.data?.filters).map(
                                    (key) =>
                                        Array.isArray(state?.data?.filters[key]) &&
                                        state?.data?.filters[key].map((item: any) => {
                                            const domainCategoryCode: string = getDomainCategoryCodeForKey(key) as string;
                                            if (!domainCategoryCode) return;
                                            const displayValue = getDomainValueForCode(item, domainCategoryCode, state);
                                            if (!displayValue) return;
                                            if (!displayClearAll) displayClearAll = true;
                                            return (
                                                <li>
                                                    <a href="#" key={`${key}-${item}`}>
                                                        {displayValue}
                                                        <i
                                                            className="icon-cross"
                                                            onClick={(event) => handleAppliedFilterClick(event, key, item)}></i>
                                                    </a>
                                                </li>
                                            );
                                        })
                                )}
                                {getActiveShowProfileFiltersFromConfig(state?.data?.filters, modifiedFilterConfig).map(
                                    ({ key, label }) => {
                                        if (!displayClearAll) displayClearAll = true;
                                        return (
                                            <li key={`show-profile-${key}`}>
                                                <a href="#">
                                                    {label}
                                                    <i
                                                        className="icon-cross"
                                                        onClick={(event) => handleBooleanFilterClear(event, key)}
                                                    />
                                                </a>
                                            </li>
                                        );
                                    }
                                )}
                                {displayClearAll && (
                                    <li className="clear">
                                        <a className="btn-clear" role="button" onClick={(e) => handleClearAllFilterClick(e, state, dispatch)}>
                                            Clear All
                                        </a>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        </div>
        </>
    );
};

export default DirectorySearchControl;
